import React, { useState, useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'
import {
  fetchRegions, fetchPages, fetchTimeline,
  fetchPlacement, fetchPlacements, fetchPreviewImpacts, fetchWidgetPlacements,
  fetchWidgets, createWidget, updateWidget, createPlacement, updatePlacement, deletePlacement,
  fetchPageDraft, listSlugs, ConflictError, ImpactResult,
  Region, PageRecord, PlacementSummary, Widget
} from '../../services/api'
import { WIDGET_TYPES, getWidgetTypeLabel } from '../../constants/widgetTypes'
import { PAGE_TYPES, getSlugTargetConfig } from '../../constants/pageTypes'
import CompetitionSelect, { CompetitionFilter } from '../../components/CompetitionSelect'
import CompetitorSelect from '../../components/CompetitorSelect'
import { Competitor } from '../../types/forms'
import { getPlacementStatus } from '../../utils/merchandising'
import { useWidgetSite } from '../../contexts/WidgetSiteContext'
import { useToast } from '../../contexts/ToastContext'
import Select from 'react-select'
import { Button, Panel, Badge, Banner, ToggleGroup, Tabs, Drawer, useSpeedbump } from '../../components'
import WidgetForm from '../../components/merchandising/WidgetForm'
import { useWidgetFormState } from '../../hooks'
import { convertUTCToPTInput, convertPTInputToUTC, formatToPacificTime, offsetPTInput } from '../../utils/timezone'
import utilStyles from '../../styles/utilities.module.scss'
import formStyles from '../../styles/forms.module.scss'
import selectStyles from '../../styles/react-select.module.scss'
import styles from './PlacementDetail.module.scss'

const ALL_DEVICES = ['desktop', 'mobile', 'tablet', 'tv']

export interface PlacementDetailInitialContext {
  pageType?: string
  regionId?: string
  sortOrder?: number
  pageId?: string
  slug?: string
  startsAt?: string
  pinnedPosition?: number | null
}

export interface PlacementDetailProps {
  isOpen: boolean
  onClose: () => void
  /** Called after a successful create/update/delete so the parent can refresh its data */
  onSaved: () => void
  /** `null` for create mode, placement UUID for edit mode */
  placementId: string | null
  /** Pre-populates form fields in create mode */
  initialContext?: PlacementDetailInitialContext
}

const PlacementDetail: React.FC<PlacementDetailProps> = ({ isOpen, onClose, onSaved, placementId, initialContext }) => {
  const { siteId } = useWidgetSite()
  const { toast } = useToast()
  const confirm = useSpeedbump()
  const isCreateMode = placementId === null

  const initialPageType = initialContext?.pageType ?? ''
  const initialRegionId = initialContext?.regionId ?? ''
  const initialSortOrder = initialContext?.sortOrder ?? 0
  const initialPageId = initialContext?.pageId ?? ''
  const initialSlug = initialContext?.slug ?? ''
  const initialStartsAt = initialContext?.startsAt ?? ''
  const initialPinnedPosition = initialContext?.pinnedPosition ?? null

  // Form state
  const [selectedPageType, setSelectedPageType] = useState(initialPageType)
  const [selectedRegionId, setSelectedRegionId] = useState(initialRegionId)
  const [selectedSortOrder, setSelectedSortOrder] = useState(initialSortOrder)
  const [selectedPageId, setSelectedPageId] = useState<string>(initialPageId)
  // Slug targeting: scopes the placement to one tournament/player. `selectedSlug`
  // is what we persist (targeting_rules.slug); `slugTarget` is the picked entity
  // shown in the search box (its tc_id resolves to the canonical slug).
  const [selectedSlug, setSelectedSlug] = useState<string>(initialSlug)
  const [slugTarget, setSlugTarget] = useState<{ tc_id: string; name: string } | null>(null)
  const [slugResolving, setSlugResolving] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [selectedWidgetId, setSelectedWidgetId] = useState('')
  const [deviceTargets, setDeviceTargets] = useState<string[]>([...ALL_DEVICES])
  const [startsAt, setStartsAt] = useState(initialStartsAt)
  const [endsAt, setEndsAt] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isPinned, setIsPinned] = useState(initialPinnedPosition != null)

  const [hasEndDate, setHasEndDate] = useState(false)

  // Inline widget creation
  const [showNewWidget, setShowNewWidget] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data: regionsData } = useSWR(
    siteId ? ['merch-regions', siteId] : null,
    () => fetchRegions(siteId),
    { revalidateOnFocus: false }
  )
  const regions: Region[] = regionsData?.regions || []

  const { data: pagesData } = useSWR(
    siteId && selectedPageType ? ['merch-pages', siteId, selectedPageType] : null,
    () => fetchPages(siteId, selectedPageType),
    { revalidateOnFocus: false }
  )
  const pages: PageRecord[] = pagesData?.pages || []

  // Fetch widgets
  const { data: widgetsData, mutate: mutateWidgets } = useSWR(
    siteId ? ['merch-widgets', siteId] : null,
    () => fetchWidgets(siteId),
    { revalidateOnFocus: false }
  )
  const widgets: Widget[] = useMemo(() => widgetsData?.widgets || [], [widgetsData])

  // Two form states:
  //   - existingState: edits an existing widget when user picks one from the dropdown
  //   - newState: creates a new widget on the "New Widget" tab (auto-creates for banner_image)
  // Each tab preserves its state independently when the user switches tabs.
  const existingState = useWidgetFormState({ siteId, initialWidget: null })
  const newState = useWidgetFormState({
    siteId,
    initialWidget: null,
    onWidgetsChanged: () => { void mutateWidgets() },
  })


  // Fetch timeline for suggested start times
  const { data: timelineData } = useSWR(
    siteId ? ['merch-timeline', siteId, selectedPageType || undefined, selectedPageId || undefined, selectedSlug || undefined] : null,
    () => fetchTimeline({ site_id: siteId, page_type: selectedPageType || undefined, page_id: selectedPageId || undefined, slug: selectedSlug || undefined }),
    { revalidateOnFocus: false }
  )

  // Fetch page draft for concurrency control
  const { data: draftData } = useSWR(
    siteId ? ['merch-page-draft', siteId, selectedPageType || undefined, selectedPageId || undefined] : null,
    () => fetchPageDraft({
      site_id: siteId,
      page_type: selectedPageType || undefined,
      page_id: selectedPageId || undefined,
    }),
    { revalidateOnFocus: false }
  )
  // Track draft token in a ref so it stays current after mutations
  const draftUpdatedAtRef = useRef<string | null>(null)
  useEffect(() => {
    draftUpdatedAtRef.current = draftData?.has_draft ? (draftData.updated_at ?? null) : ''
  }, [draftData])
  const getDraftToken = () => draftUpdatedAtRef.current ?? ''

  // Fetch other placements at the same position for the timeline view
  const { data: positionPlacementsData, mutate: mutatePositionPlacements } = useSWR(
    siteId && selectedRegionId ? ['merch-placements', siteId, { region_id: selectedRegionId, page_type: selectedPageType || undefined, page_id: selectedPageId || undefined, slug: selectedSlug || undefined }] : null,
    () => fetchPlacements({
      site_id: siteId,
      region_id: selectedRegionId,
      page_type: selectedPageType || undefined,
      page_id: selectedPageId || undefined,
      slug: selectedSlug || undefined,
    }),
    { revalidateOnFocus: false }
  )
  const positionPlacements = useMemo(() => {
    const all = positionPlacementsData?.placements || []
    return all
      .filter(p => p.id !== placementId && p.sort_order === selectedSortOrder)
      .sort((a, b) => {
        const aMs = a.starts_at ? a.starts_at.getTime() : 0
        const bMs = b.starts_at ? b.starts_at.getTime() : 0
        return aMs - bMs
      })
  }, [positionPlacementsData, placementId, selectedSortOrder])

  // In create mode, inherit pinned state from siblings if not explicitly set via query param
  useEffect(() => {
    if (isCreateMode && initialPinnedPosition == null && positionPlacements.length > 0) {
      const siblingPinned = positionPlacements.some(p => p.pinned_position != null)
      if (siblingPinned) setIsPinned(true)
    }
  }, [isCreateMode, initialPinnedPosition, positionPlacements])

  // Compute impacts
  const impactStartsAt = startsAt ? convertPTInputToUTC(startsAt) : undefined
  const impactEndsAt = hasEndDate && endsAt ? convertPTInputToUTC(endsAt) : undefined
  const { data: impactData } = useSWR(
    selectedRegionId ? ['merch-preview-impacts', selectedRegionId, selectedSortOrder, selectedPageType, selectedPageId, selectedSlug, impactStartsAt, impactEndsAt, placementId] : null,
    () => fetchPreviewImpacts({
      region_id: selectedRegionId,
      sort_order: selectedSortOrder,
      page_type: selectedPageType || undefined,
      page_id: selectedPageId || undefined,
      slug: selectedSlug || undefined,
      starts_at: impactStartsAt,
      ends_at: impactEndsAt,
      exclude_placement_id: isCreateMode ? undefined : placementId,
    }),
    { revalidateOnFocus: false }
  )
  const impactSummary: ImpactResult[] = impactData?.impacts || []

  // Fetch existing placement for edit mode
  const { data: existingPlacement, isLoading, mutate: mutateExistingPlacement } = useSWR(
    !isCreateMode && placementId ? ['merch-placement', placementId] : null,
    () => fetchPlacement(placementId!),
    { revalidateOnFocus: false }
  )

  // Fetch how many other placements use this widget (edit mode only — drives the "Used in N other placements" hint)
  const { data: widgetPlacementsData } = useSWR(
    !isCreateMode && selectedWidgetId ? ['merch-widget-placements', selectedWidgetId] : null,
    () => fetchWidgetPlacements(selectedWidgetId),
    { revalidateOnFocus: false }
  )
  const otherPlacementsCount = useMemo(() => {
    if (!widgetPlacementsData) return 0
    return widgetPlacementsData.placements.filter(p => p.id !== placementId).length
  }, [widgetPlacementsData, placementId])

  // Populate form from existing placement
  useEffect(() => {
    if (existingPlacement && !isCreateMode) {
      const p = existingPlacement as PlacementSummary
      setSelectedPageType(p.page_type || '')
      setSelectedRegionId(p.region.id)
      setSelectedSortOrder(p.sort_order)
      setSelectedPageId(p.page?.id || '')
      setSelectedSlug(p.slug || '')
      setSlugTarget(null)
      setSelectedWidgetId(p.widget.id)
      existingState.reset(p.widget as unknown as Widget)
      setDeviceTargets(p.device_targets)
      setStartsAt(p.starts_at ? convertUTCToPTInput(p.starts_at) : '')
      setEndsAt(p.ends_at ? convertUTCToPTInput(p.ends_at) : '')
      setHasEndDate(!!p.ends_at)
      setIsActive(p.is_active)
      setIsPinned(p.pinned_position != null)
    }
    // existingState.reset is stable via useCallback — excluded to avoid
    // re-running on unrelated form edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPlacement, isCreateMode])

  const handleDeviceToggle = (device: string) => {
    setDeviceTargets(prev =>
      prev.includes(device) ? prev.filter(d => d !== device) : [...prev, device]
    )
  }

  const handleSave = async () => {
    if (!selectedRegionId) { setActionError('Please select a region'); return }
    if (!showNewWidget && !selectedWidgetId) { setActionError('Please select a widget'); return }
    if (showNewWidget && (!newState.type || !newState.name.trim())) { setActionError('Widget type and name are required'); return }
    if (!showNewWidget && selectedWidgetId && !existingState.name.trim()) { setActionError('Widget name is required'); return }
    if (deviceTargets.length === 0) { setActionError('Select at least one device target'); return }
    if (supportsSlug && slugResolving) { setActionError('Still resolving the selected page — please wait a moment'); return }
    if (supportsSlug && slugTarget && !selectedSlug) { setActionError(slugError || 'The selected page has no published URL to target'); return }

    // Check if widget is being modified and used elsewhere — confirm with user.
    // widget_type counts as a modification (a carousel size/orientation change),
    // so a size-only edit still trips the shared-widget confirmation.
    if (!showNewWidget && selectedWidgetId && selectedWidget) {
      const nameChanged = existingState.name.trim() !== selectedWidget.name
      const typeChanged = existingState.type !== selectedWidget.widget_type
      const metaChanged = JSON.stringify(existingState.metadata) !== JSON.stringify(selectedWidget.metadata || {})
      if (nameChanged || typeChanged || metaChanged) {
        try {
          const data = await fetchWidgetPlacements(selectedWidgetId)
          if (data.placements.length > 0) {
            const current = data.placements.filter(p => p.is_currently_active)
            const upcoming = data.placements.filter(p => !p.is_currently_active)
            const ok = await confirm({
              title: data.placements.length > 1 ? 'This widget is used in multiple placements' : 'Modify widget?',
              confirmLabel: 'Save Changes',
              ...(data.placements.length <= 1
                ? { message: "You're changing this widget. No other placements use it — only this one will be affected." }
                : {
                  children: (
                    <>
                      {current.length > 0 && (
                        <div>
                          <strong>Current placements ({current.length}):</strong>
                          <ul>
                            {current.map(p => (
                              <li key={p.id}>
                                {p.id === placementId ? <em>{p.name} (this placement)</em> : <>{p.name}</>}
                                {p.page ? ` — ${p.page.name}` : p.page_type ? ` — ${p.page_type}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {upcoming.length > 0 && (
                        <div>
                          <strong>Upcoming placements ({upcoming.length}):</strong>
                          <ul>
                            {upcoming.map(p => (
                              <li key={p.id}>
                                {p.id === placementId ? <em>{p.name} (this placement)</em> : <>{p.name}</>}
                                {p.page ? ` — ${p.page.name}` : p.page_type ? ` — ${p.page_type}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p>Your changes to this widget will affect all of these placements.</p>
                    </>
                  ),
                }),
            })
            if (!ok) return
          }
        } catch { /* proceed if lookup fails */ }
      }
    }

    const startUtc = startsAt ? convertPTInputToUTC(startsAt) : null
    const endUtc = hasEndDate && endsAt ? convertPTInputToUTC(endsAt) : null

    if (startUtc && endUtc && endUtc <= startUtc) {
      setActionError('End time must be after start time')
      return
    }

    setSaving(true)
    setActionError(null)

    let widgetId = selectedWidgetId

    if (showNewWidget) {
      // New widget tab. The widget may already exist if auto-create fired
      // (banner_image): in that case we only PUT any pending edits. Otherwise
      // POST a fresh widget.
      try {
        if (newState.widgetId) {
          await updateWidget(newState.widgetId, {
            name: newState.name.trim(),
            metadata: newState.metadata,
          })
          widgetId = newState.widgetId
        } else {
          const created = await createWidget({
            site_id: siteId,
            widget_type: newState.type,
            name: newState.name.trim(),
            metadata: newState.metadata,
          })
          widgetId = created.id
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to create widget')
        setSaving(false)
        return
      }
    } else if (selectedWidgetId && selectedWidget) {
      // Update existing widget if anything changed. widget_type can change too:
      // a carousel's size/orientation selector edits it in place.
      const nameChanged = existingState.name.trim() !== selectedWidget.name
      const typeChanged = existingState.type !== selectedWidget.widget_type
      const metaChanged = JSON.stringify(existingState.metadata) !== JSON.stringify(selectedWidget.metadata || {})
      if (nameChanged || typeChanged || metaChanged) {
        try {
          await updateWidget(selectedWidgetId, {
            name: existingState.name.trim(),
            // Only send widget_type when it actually changed, so a name/metadata
            // edit of a widget with a legacy/unlisted type isn't rejected.
            ...(typeChanged ? { widget_type: existingState.type } : {}),
            metadata: existingState.metadata,
          })
        } catch (err) {
          setActionError(err instanceof Error ? err.message : 'Failed to update widget')
          setSaving(false)
          return
        }
      }
    }

    const expectedImpacts = impactSummary.map(impact => ({
      type: impact.type,
      placement_id: impact.placement_id,
      new_starts_at: impact.new_starts_at,
      new_ends_at: impact.new_ends_at,
    }))

    const payload: Parameters<typeof createPlacement>[0] = {
      region_id: selectedRegionId,
      widget_id: widgetId,
      sort_order: selectedSortOrder,
      pinned_position: isPinned ? selectedSortOrder : null,
      page_id: selectedPageId || null,
      page_type: selectedPageType || null,
      slug: supportsSlug ? (selectedSlug || null) : null,
      device_targets: deviceTargets,
      starts_at: startUtc,
      ends_at: endUtc,
      is_active: isActive,
      expected_impacts: expectedImpacts,
      draft_updated_at: getDraftToken(),
    }

    try {
      if (isCreateMode) {
        await createPlacement(payload)
        toast('Placement created' + (expectedImpacts.length > 0 ? ` (${expectedImpacts.length} existing placement${expectedImpacts.length > 1 ? 's' : ''} adjusted)` : ''))
      } else {
        await updatePlacement(placementId!, payload)
        toast('Placement saved')
      }
      // Placement save succeeded — mark widget state committed so closing
      // the drawer doesn't delete the auto-created widget.
      newState.markCommitted()
      existingState.markCommitted()
      // Invalidate the caches PlacementDetail reads on reopen, so the
      // next mount sees the widget edits we just made instead of stale data.
      await Promise.all([mutateWidgets(), mutateExistingPlacement()])
      onSaved()
      onClose()
    } catch (err) {
      // Placement save failed after widget create/update — roll back any
      // auto-created widget so we don't leak an orphan.
      newState.discardIfAutoCreated()
      if (err instanceof ConflictError) {
        if (err.data?.computed_impacts) {
          mutatePositionPlacements()
          setActionError('The position has changed since you last loaded it. Please review the updated impacts and try again.')
        } else {
          setActionError(err.message || 'This page was modified by another user. Please go back and try again.')
        }
      } else {
        setActionError(err instanceof Error ? err.message : 'Failed to save placement')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete placement',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      message: 'Delete this placement? This will be staged as a draft change and take effect when you publish.',
    })
    if (!ok) return
    try {
      await deletePlacement(placementId!, getDraftToken())
      onSaved()
      onClose()
    } catch (err) {
      if (err instanceof ConflictError) {
        setActionError(err.message || 'This page was modified by another user. Please go back and try again.')
      } else {
        setActionError(err instanceof Error ? err.message : 'Failed to delete placement')
      }
    }
  }

  // Group widgets by type for the selector
  const widgetOptions = useMemo(() => {
    const grouped: Record<string, { value: string; label: string }[]> = {}
    for (const w of widgets) {
      const typeLabel = WIDGET_TYPES.find(wt => wt.code === w.widget_type)?.label || w.widget_type
      if (!grouped[typeLabel]) grouped[typeLabel] = []
      grouped[typeLabel].push({ value: w.id, label: w.name })
    }
    return Object.entries(grouped).map(([label, options]) => ({
      label,
      options,
    }))
  }, [widgets])

  const selectedWidget = widgets.find(w => w.id === selectedWidgetId)

  const slugConfig = getSlugTargetConfig(selectedPageType)
  const supportsSlug = slugConfig !== null

  // Resolve the picked tournament/player to its canonical URL slug — that's the
  // slug ACE matches against when a request comes in for /tournaments/{slug}.
  const handleSlugTargetPick = async (target: { tc_id: string; name: string } | null) => {
    setSlugError(null)
    setSlugTarget(target)
    if (!target || !slugConfig) {
      setSelectedSlug('')
      return
    }
    setSlugResolving(true)
    try {
      const res = await listSlugs(slugConfig.slugEntityType, target.tc_id)
      if (res.canonical?.slug) {
        setSelectedSlug(res.canonical.slug)
      } else {
        setSelectedSlug('')
        setSlugError(`No published URL found for "${target.name}". It can't be targeted yet.`)
      }
    } catch {
      setSelectedSlug('')
      setSlugError('Failed to resolve the page URL for this selection.')
    } finally {
      setSlugResolving(false)
    }
  }

  const clearSlugTarget = () => {
    setSlugTarget(null)
    setSelectedSlug('')
    setSlugError(null)
  }

  const drawerTitle = isCreateMode ? 'Create Placement' : 'Edit Placement'

  // Wrap onClose so a drawer-close discards any auto-created widget that
  // hasn't been committed via a successful save.
  const handleClose = () => {
    newState.discardIfAutoCreated()
    onClose()
  }

  const drawerFooter = (
    <>
      {!isCreateMode && (
        <Button variant="danger" onClick={handleDelete}>Delete</Button>
      )}
      <div className={styles.footerRight}>
        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isCreateMode ? 'Create Placement' : 'Save Changes'}
        </Button>
      </div>
    </>
  )

  if (!isCreateMode && isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={handleClose} title={drawerTitle}>
        <div>Loading placement...</div>
      </Drawer>
    )
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title={drawerTitle} footer={drawerFooter}>
      {actionError && (
        <Banner variant="error" message={actionError} onDismiss={() => setActionError(null)} />
      )}

      <div>
        {/* Location breadcrumb */}
        <div className={styles.locationBreadcrumb}>
          <span>{(() => {
            if (!selectedPageType) return 'All Pages'
            if (selectedPageId) {
              const p = pages.find(p => p.id === selectedPageId)
              return p ? p.name : 'Loading...'
            }
            return PAGE_TYPES.find(pt => pt.value === selectedPageType)?.label || selectedPageType
          })()}</span>
          {supportsSlug && selectedSlug && (
            <>
              <span className={styles.breadcrumbSep}>/</span>
              <span>{slugTarget?.name || selectedSlug}</span>
            </>
          )}
          <span className={styles.breadcrumbSep}>/</span>
          <span>{(() => { const r = regions.find(r => r.id === selectedRegionId); return r ? r.name : 'Loading...' })()}</span>
          <span className={styles.breadcrumbSep}>/</span>
          <span>Position {selectedSortOrder + 1}</span>
        </div>

        {/* Widget Section */}
        <Panel>
          {!isCreateMode && (
            <Panel.Header
              trailing={selectedWidget
                ? <Badge variant="secondary">{getWidgetTypeLabel(selectedWidget.widget_type)}</Badge>
                : undefined}
            >
              <h3>Widget</h3>
            </Panel.Header>
          )}

          {/* Widget picker — create mode only. Tabs serve as the panel header visually. In edit mode the widget is fixed (use "Schedule replacement" to change it). */}
          {isCreateMode && (
            <Tabs
              tabs={[
                { id: 'existing', label: 'Existing Widget' },
                { id: 'new', label: 'New Widget' },
              ]}
              activeTab={showNewWidget ? 'new' : 'existing'}
              onTabChange={(v) => {
                if (v === 'new') {
                  setShowNewWidget(true)
                  setSelectedWidgetId('')
                  existingState.reset(null)
                } else {
                  setShowNewWidget(false)
                  // Abandoning the new-widget tab — clean up any auto-created
                  // banner widget we may have created early.
                  newState.discardIfAutoCreated()
                  newState.reset(null)
                }
              }}
            >
              {!showNewWidget && (
                <Select
                  value={selectedWidgetId ? { value: selectedWidgetId, label: selectedWidget?.name || selectedWidgetId } : null}
                  onChange={(opt) => {
                    const id = opt?.value || ''
                    setSelectedWidgetId(id)
                    const w = widgets.find(w => w.id === id)
                    existingState.reset(w ?? null)
                  }}
                  options={widgetOptions}
                  placeholder="Search widgets..."
                  isSearchable
                  className={selectStyles.reactSelect}
                  classNamePrefix="react-select"
                />
              )}

              {showNewWidget && (
                <div className={utilStyles['mt-md']}>
                  <WidgetForm state={newState} />
                </div>
              )}
            </Tabs>
          )}

          {/* Existing-widget editor — renders after dropdown selection in create mode, or always in edit mode. */}
          {!showNewWidget && selectedWidget && (
            <div className={utilStyles['mt-md']}>
              <WidgetForm state={existingState} />
              {!isCreateMode && otherPlacementsCount > 0 && (
                <div className={styles.widgetUsageHint}>
                  This widget is also used in {otherPlacementsCount} other placement{otherPlacementsCount > 1 ? 's' : ''}. Changes here affect all of them.
                </div>
              )}
            </div>
          )}

          {!isCreateMode && !selectedWidget && <div>Loading widget...</div>}
        </Panel>

        {/* Schedule & Targeting */}
        <Panel>
          <Panel.Header><h3>Schedule</h3></Panel.Header>

          <div className={formStyles.formField}>
            {/* Position timeline */}
            {selectedRegionId && positionPlacements.length > 0 && (
              <div className={styles.slotTimeline}>
                <div className={styles.slotTimelineLabel}>This position&apos;s schedule:</div>
                <div className={styles.timelineBlocks}>
                  {positionPlacements.filter(p => p.is_active).map(p => {
                    const status = getPlacementStatus(p)
                    const isImpacted = impactSummary.some(i => i.placement_id === p.id)
                    return (
                      <div
                        key={p.id}
                        className={`${styles.timelineBlock} ${styles[`timelineBlock_${status.label.toLowerCase()}`]} ${isImpacted ? styles.timelineBlockTarget : ''}`}
                      >
                        <div className={styles.timelineBlockName}>{p.name}</div>
                        <div className={styles.timelineBlockTime}>
                          {!p.starts_at ? 'Always on' : formatToPacificTime(p.starts_at)}
                          {' → '}
                          {!p.ends_at ? 'No end' : formatToPacificTime(p.ends_at)}
                        </div>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Starts */}
            <div className={formStyles.fieldGroup}>
              <label>Starts</label>
              <ToggleGroup
                variant="toggle"
                size="small"
                options={[
                  { value: 'now', label: 'Now' },
                  { value: 'scheduled', label: 'Scheduled' },
                ]}
                value={startsAt ? 'scheduled' : 'now'}
                onChange={(v) => {
                  if (v === 'now') {
                    setStartsAt('')
                  } else if (!startsAt) {
                    setStartsAt(offsetPTInput(null, 1))
                  }
                }}
              />
              {startsAt && (
                <>
                  <div className={styles.dateInputRow}>
                    <input
                      type="datetime-local"
                      value={startsAt}
                      onChange={(e) => setStartsAt(e.target.value)}
                      className={styles.dateInput}
                    />
                    <span className={styles.tzLabel}>Pacific Time</span>
                  </div>
                  {/* Suggested times */}
                  {(() => {
                    const suggestions: { ts: string; ptValue: string; title: string }[] = []
                    for (const p of positionPlacements.filter(sp => sp.is_active && sp.ends_at)) {
                      suggestions.push({
                        ts: p.ends_at!.toISOString(),
                        ptValue: convertUTCToPTInput(p.ends_at!),
                        title: `After "${p.name}" ends`,
                      })
                    }
                    if (timelineData) {
                      for (const cp of timelineData.change_points) {
                        const desc = cp.events.map(e =>
                          `${e.placement_name} ${e.event_type === 'starts' ? 'starts' : 'ends'} in ${e.region_name} #${e.position + 1}`
                        ).join('; ')
                        suggestions.push({ ts: cp.timestamp, ptValue: convertUTCToPTInput(cp.timestamp), title: desc })
                      }
                    }
                    const seen = new Set<string>()
                    const unique = suggestions
                      .sort((a, b) => a.ts.localeCompare(b.ts))
                      .filter(s => { if (seen.has(s.ptValue)) return false; seen.add(s.ptValue); return true })
                      .slice(0, 4)
                    if (unique.length === 0) return null
                    return (
                      <div className={styles.suggestions}>
                        {unique.map(s => (
                          <button
                            key={s.ptValue}
                            type="button"
                            title={s.title}
                            className={`${styles.suggestion} ${startsAt === s.ptValue ? styles.suggestionActive : ''}`}
                            onClick={() => setStartsAt(s.ptValue)}
                          >
                            {formatToPacificTime(s.ts)}
                          </button>
                        ))}
                      </div>
                    )
                  })()}
                </>
              )}
            </div>

            {/* Ends */}
            <div className={formStyles.fieldGroup}>
              <label>Ends</label>
              <ToggleGroup
                variant="toggle"
                size="small"
                options={[
                  { value: 'none', label: 'No end date' },
                  { value: 'date', label: 'End date' },
                ]}
                value={hasEndDate ? 'date' : 'none'}
                onChange={(v) => {
                  if (v === 'none') {
                    setHasEndDate(false)
                    setEndsAt('')
                  } else {
                    setHasEndDate(true)
                    if (!endsAt) setEndsAt(offsetPTInput(startsAt || null, 14))
                  }
                }}
              />
              {hasEndDate && (
                <div className={styles.dateInputRow}>
                  <input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className={styles.dateInput}
                  />
                  <span className={styles.tzLabel}>Pacific Time</span>
                </div>
              )}
            </div>

            {/* Impact summary */}
            {impactSummary.length > 0 && (
              <div className={styles.impactSummary}>
                <div className={styles.impactSummaryLabel}>
                  Saving will adjust {impactSummary.length} existing placement{impactSummary.length > 1 ? 's' : ''}:
                </div>
                <ul className={styles.impactList}>
                  {impactSummary.map(impact => (
                    <li key={impact.placement_id} className={styles[`impact_${impact.type}`]}>
                      {impact.type === 'trim_end' && (
                        <>End <a href={`?placement=${impact.placement_id}`} target="_blank" rel="noopener noreferrer">{impact.placement_name}</a> early at {formatToPacificTime(impact.new_ends_at)}</>
                      )}
                      {impact.type === 'push_start' && (
                        <>Push <a href={`?placement=${impact.placement_id}`} target="_blank" rel="noopener noreferrer">{impact.placement_name}</a> start to {formatToPacificTime(impact.new_starts_at)}</>
                      )}
                      {impact.type === 'swallowed' && (
                        <>Deactivate <a href={`?placement=${impact.placement_id}`} target="_blank" rel="noopener noreferrer">{impact.placement_name}</a> (fully covered by this placement)</>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Status */}
            <div className={formStyles.fieldGroup}>
              <label>Status</label>
              <div className={styles.statusDevicesRow}>
                <label className={formStyles.checkboxLabel}>
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  Active
                </label>
                <label className={formStyles.checkboxLabel}>
                  <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
                  Pinned
                </label>
              </div>
            </div>

            {/* Devices */}
            <div className={formStyles.fieldGroup}>
              <label>Devices</label>
              <div className={styles.statusDevicesRow}>
                {ALL_DEVICES.map(d => (
                  <label key={d} className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={deviceTargets.includes(d)}
                      onChange={() => handleDeviceToggle(d)}
                    />
                    {d.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>

            {/* Targeting — pick a specific tournament/player, or leave blank for all */}
            {supportsSlug && slugConfig && (
              <div className={formStyles.fieldGroup}>
                <label>Specific {slugConfig.noun} (optional)</label>
                {selectedSlug && !slugTarget ? (
                  <div className={styles.statusDevicesRow}>
                    <Badge variant="secondary">Targeting: {selectedSlug}</Badge>
                    <Button variant="secondary" size="small" onClick={clearSlugTarget}>
                      Change
                    </Button>
                  </div>
                ) : slugConfig.kind === 'competition' ? (
                  <CompetitionSelect
                    value={slugTarget ? ({ tc_id: slugTarget.tc_id, name: slugTarget.name } as CompetitionFilter) : null}
                    onChange={(c) => {
                      const sel = c as CompetitionFilter | null
                      void handleSlugTargetPick(sel ? { tc_id: sel.tc_id, name: sel.name } : null)
                    }}
                    placeholder="Search tournaments…"
                  />
                ) : (
                  <CompetitorSelect
                    value={slugTarget ? ({ tc_id: slugTarget.tc_id, name: slugTarget.name } as Competitor) : null}
                    onChange={(c) => {
                      const sel = c as Competitor | null
                      void handleSlugTargetPick(sel ? { tc_id: sel.tc_id, name: sel.name } : null)
                    }}
                    placeholder="Search players…"
                  />
                )}
                <div className={styles.widgetUsageHint}>
                  {slugResolving
                    ? 'Resolving page URL…'
                    : slugError
                      ? slugError
                      : `Leave blank to show on all ${slugConfig.noun} pages.`}
                </div>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </Drawer>
  )
}

export default PlacementDetail
